<template>
  <div
    id="project_tree"
    class=" project_tree ProjectTree"
    :style="{ width: width_px }"
    :class="{ project_tree_tight: $ui.tight_mode, 'is-dragging': (dragging || isDragging) }"
    @mouseenter="display_arrow = true"
    @mouseleave="display_arrow = false"
  >
    <ProjectTreeIndicatorArrow v-if="$ui.tight_mode && display_arrow" />
    <ProjectTreeFilter v-model="filter_text" :tight_mode="$ui.tight_mode" />
    <span v-if="hovered_area != null">{{ hovered_area.serial }}</span>

    <div
      id="project_tree_main"
      class="project_tree_main"
      v-if="$app.sltd.project"
      @dragover.prevent="updateDropEffect"
    >
      <div class="nobreak">
        <TreeOpenCloseBtn v-model="$app.sltd.project.app_prop.tree_open" />
        <div
          class="project_drop_target"
          @dragover.prevent
          @drop="onProjectDrop"
          :class="{
            'drop-enabled': dragged_type === 'virtual_controller' || dragged_type === 'cntrldevice',
            'drop-disabled': dragging && !(dragged_type === 'virtual_controller' || dragged_type === 'cntrldevice')
          }"
        >
          <ProjectTreeItem
            :item="$app.sltd.project"
            route_to="project"
            :route_params="{ project_id: $app.sltd.project.id }"
            @include_click="include_item($app.sltd.project)"
          />
          <IconToggle
            @click="
              $app.sltd.project.toggle_all_tree_open(expand_or_collapse)
            "
            v-model="expand_or_collapse"
            icon_open="unfold_less"
            icon_closed="unfold_more"
            :hint_open="$txt.collapse_all"
            :hint_closed="$txt.expand_all"
          />
          <PadlockToggle
            size="20"
            v-if="$app.sltd.project.dto.canSetup"
            v-model="$app.sltd.project.app_prop.edit_lock"
            :title="padlock_hint"
            @lock="lockProject()"
          />
        </div>
      </div>

      <ul class="project_tree_ul" v-show="$app.sltd.project.app_prop.tree_open">
        <transition-group name="fade">
          <li
            v-for="controller in sorted_cntrls" :key="controller.id"
            class="project_tree_li"
            data-kind="controller"
            :data-id="controller.id"
            @dragover.prevent
            @drop="onControllerDrop($event, controller)"
            :class="{
              project_tree_drop_area_hover:
                $appm.can_drop_dragged_on_target(controller) && hovered_area == controller,
              'drop-enabled':
                ((dragged_cntrl_id) && (dragged_zone_id == null && dragged_node_id == null))
                || (dragged_type === 'virtual_zone' || dragged_type === 'cntrldevice' || (dragged_type === 'nodedevice' && controller.id === (cntrl_of_node(dragged_node_id)?.id ?? null))),
              'drop-disabled':
                dragging
                && (dragged_type !== 'virtual_node')
                && !(
                  ((dragged_cntrl_id) && (dragged_zone_id == null && dragged_node_id == null))
                  || (dragged_type === 'virtual_zone' || dragged_type === 'cntrldevice' || (dragged_type === 'nodedevice' && controller.id !== (cntrl_of_node(dragged_node_id)?.id ?? null))) 
                )
            }"
          >
            <p class="project_tree_invisiblen_zone_text" v-if="filtered">
              {{ controller.name }}
            </p>
            
            <div
              class="controller-row project_tree_row"
              :class="{
                'drop-dim-row':
                  dragged_type === 'virtual_node'
                  || dragged_type === 'nodedevice'
                  || (
                    (dragged_zone_id || dragged_node_id) &&
                    controller.id !== drag_origin_controller_id
                  )
              }"
            >
              <TreeOpenCloseBtn
                :modelValue="get_open(controller)"
                @update:modelValue="toggle_zone_open(controller, $event)"
              />
  
              <ProjectTreeItem
                :draggable= "!padlocked"
                @dragstart="!padlocked && onControllerDragStart($event, controller)"
                @dragend="onDragEnd"
                :item="controller"
                route_to="controller"
                :route_params="{
                  project_id: $app.sltd.project.id,
                  controller_id: controller.id
                }"
                @include_click="include_item(controller)"
              />
            </div>

            <ul class="project_tree_ul" v-show="controller.app_prop.tree_open">
              <transition-group name="fade">
                <li
                  v-for="zone in zones_for_controller(controller)" :key="zone.id"
                  class="project_tree_li"
                  data-kind="zone"
                  :data-id="zone.id"
                  @dragover.prevent
                  @drop="onZoneDrop($event, zone)"
                  :class="{
                    'drop-enabled':
                      (dragged_zone_id && ((zone.dto?.controllerId ?? zone.controllerId) === (dragged_zone_id?.dto?.controllerId ?? dragged_zone_id?.controllerId)))
                      || (dragged_node_id && ((zone.dto?.controllerId ?? zone.controllerId) === (cntrl_of_node(dragged_node_id)?.id ?? null)))
                      || (dragged_type === 'virtual_node')
                      || (dragged_type === 'nodedevice'  && ((zone.dto?.controllerId ?? zone.controllerId) === (cntrl_of_node(this.$app.dragged_item).id))),

                    'drop-dim-row':
                      (dragged_cntrl_id || dragged_type === 'virtual_controller'
                        || (dragged_node_id && ((zone.dto?.controllerId ?? zone.controllerId) !== (cntrl_of_node(dragged_node_id)?.id ?? null)))
                        || (dragged_zone_id && ((zone.dto?.controllerId ?? zone.controllerId) !== (dragged_zone_id?.dto?.controllerId ?? dragged_zone_id?.controllerId))))
                      && (dragged_type !== 'virtual_node' || dragged_type !== 'nodedevice'),  // keep zones bright for virtual-node drags

                    'drop-disabled': dragging && !(
                      (dragged_zone_id && ((zone.dto?.controllerId ?? zone.controllerId) === (dragged_zone_id?.dto?.controllerId ?? dragged_zone_id?.controllerId)))
                      || (dragged_node_id && ((zone.dto?.controllerId ?? zone.controllerId) === (cntrl_of_node(dragged_node_id)?.id ?? null)))
                      || (dragged_type === 'virtual_node')
                      || (dragged_type === 'nodedevice' && (zone.dto.controllerId) === (cntrl_of_node(this.$app.dragged_item).id))
                    ) || dragged_cntrl_id
                  }"
                >
                  <p class="project_tree_invisiblen_zone_text" v-if="filtered">
                    {{ zone.name }}
                  </p>

                  <span v-show="match_filter(zone)" class="project_tree_row">
                    <TreeOpenCloseBtn
                      :modelValue="get_open(zone)"
                      v-if="!filtered"
                      @update:modelValue="toggle_zone_open(zone, $event)"
                    />
                    <div v-else class="project_tree_spacer">&nbsp;</div>

                    <ProjectTreeItem
                      :item="zone"
                      route_to="zone"
                      :route_params="{
                        project_id: $app.sltd.project.id,
                        zone_id: zone.id
                      }"
                      :parent_include="true"
                      :node_include="zone.include_select"
                      @include_click="include_item(zone)"
                      :draggable="!padlocked"
                      @dragstart="!padlocked && onZoneDragStart($event, zone)"
                      @dragend="onDragEnd"
                    />
                  </span>
                  <ul
                    class="project_tree_ul"
                    v-show="zone.tree_open || (!filtered && filter_text != '')"
                  >
                    <li
                      v-for="node in sorted_nodes_for_zone(zone)" :key="node.id"
                      data-kind="node"
                      :data-id="node.id"
                      class="project_tree_li"
                      @dragover.prevent
                      @drop.prevent.stop="onNodeDrop($event, node, zone)"
                      :class="{
                        'drop-enabled':
                          dragged_node_id &&
                          (cntrl_of_node(node)?.id ?? null) === (cntrl_of_node(dragged_node_id)?.id ?? null),

                        'drop-dim-row':
                          dragged_cntrl_id
                          || dragged_type === 'virtual_node'
                          || (dragged_node_id && ((cntrl_of_node(node)?.id ?? null) !== (cntrl_of_node(dragged_node_id)?.id ?? null))),

                        'drop-disabled':
                          (dragging && dragged_type === 'virtual_node')
                          || (
                            (dragged_cntrl_id || dragged_zone_id || dragged_node_id) &&
                            !(
                              dragged_node_id &&
                              (cntrl_of_node(node)?.id ?? null) === (cntrl_of_node(dragged_node_id)?.id ?? null)
                            )
                          )
                          || (dragging && dragged_type === 'nodedevice' 
                          && (node.serial || (node.capability !== $app.dragged_item.capability)) 
                          && (cntrl_of_node(node)?.id ?? null) !== (cntrl_of_node(dragged_node_id)?.id ?? null))
                      }"
                    >
                      <span v-show="match_filter(node)" class="project_tree_row">
                        <div class="project_tree_spacer">&nbsp;</div>
                        <ProjectTreeItem
                          :item="node"
                          route_to="node"
                          :route_params="{
                            project_id: $app.sltd.project.id,
                            zone_id: node.dto.zoneId,
                            node_id: node.id
                          }"
                          @include_click="include_item(node)"
                          :draggable="!padlocked"
                          @dragstart="!padlocked && onNodeDragStart($event, node)"
                          @dragend="onDragEnd"
                        />
                      </span>
                    </li>
                    <ProjectTreeBtn
                      v-if="!filtered && !padlocked"
                      :title="$txt.new_node"
                      :add="true"
                      @click="new_node(zone.id, $app.sltd.prj_id)"
                      @drop_on_btn="
                        new_node_from_drop(zone.id, $app.sltd.prj_id)
                      "
                      :dragged_type="dragged_type"
                      :accept_drop_type="['nodedevice', 'virtual_node']"
                      :cntrl_device_id="controller.serial"
                    />
                  </ul>
                  <hr v-if="!match_filter(zone)" class="tight-hr" />
                </li>
              </transition-group>
              <ProjectTreeBtn
                v-if="!filtered && !padlocked"
                :add="true"
                :title="$txt.new_zone"
                @drop_on_btn="
                  new_zone_from_drop($app.sltd.prj_id, controller.id)
                "
                @click="new_zone(controller)"
                :dragged_type="dragged_type"
                :accept_drop_type="['node', 'virtual_zone']"
              />
            </ul>
          </li>
        </transition-group>
      </ul>
      <ProjectTreeBtn
        v-if="!filtered && !padlocked"
        :add="true"
        :title="$txt.new_controller"
        @click="new_controller($app.sltd.project)"
        @drop_on_btn="new_controller_from_device($app.sltd.prj_id)"
        :dragged_type="dragged_type"
        :accept_drop_type="['cntrldevice', 'virtual_controller']"
      />
    </div>

    <Modal
      :title="$txt.new_zone"
      v-if="new_zone_modal"
      @close="new_zone_modal = false"
    >
      <LabeledInput :label="$txt.name" v-model="new_item_name" />
      <DropDown
        v-model="new_zc_id"
        :title="$txt.cntrl"
        :opts="available_cntrls"
      />

      <template v-slot:footer>
        <Btn
          :title="$txt.create"
          @clicked="
            create_zone();
            new_zone_modal = false;
          "
          :disabled="(new_item_name.length < 1 || new_zc_id == 0) ? true : null"
          data-default-enter
        />
        <Btn
          class="hol_btn"
          :title="$txt.cancel"
          @clicked="new_zone_modal = false"
          data-default-esc
        />
      </template>
    </Modal>

    <Modal
      :title="$txt.new_controller"
      v-if="new_ctrl_modal"
      @close="new_ctrl_modal = false"
    >
      <LabeledInput :label="$txt.name" v-model="new_item_name" />

      <template v-slot:footer>
        <Btn
          :title="$txt.create"
          @clicked="
            create_ctrl($app.sltd.prj_id);
            new_ctrl_modal = false;
          "
          :disabled="(new_item_name.length < 1) ? true : null"
          data-default-enter
        />
        <Btn
          class="hol_btn"
          :title="$txt.cancel"
          @clicked="new_ctrl_modal = false"
          data-default-esc
        />
      </template>
    </Modal>

    <Modal
      :title="$txt.new_node"
      v-if="new_node_modal"
      @close="new_node_modal = false"
    >
      <LabeledInput :label="$txt.name" v-model="new_item_name" />
      <EnumDropDown
        :title="$txt.type"
        v-model="new_node_type"
        :enum_to_choose_from="$camur.node_type"
      ></EnumDropDown>

      <template v-slot:footer>
        <Btn
          :title="$txt.create"
          @clicked="
            create_node();
            new_node_modal = false;
          "
          :disabled="(new_item_name.length < 1) ? true : null"
          data-default-enter
        />
        <Btn
          class="hol_btn"
          :title="$txt.cancel"
          @clicked="new_node_modal = false"
          data-default-esc
        />
      </template>
    </Modal>
  </div>
</template>

<script>
import ProjectTreeItem from "./ProjectTreeItem";
import ProjectTreeBtn from "./ProjectTreeBtn";
import ProjectTreeFilter from "./ProjectTreeFilter";
import ProjectTreeIndicatorArrow from "./ProjectTreeIndicatorArrow";
import TreeOpenCloseBtn from "./TreeOpenCloseBtn";

// A private type rather than text/plain. Chrome treats a drag carrying text or a URL as
// content the browser itself can accept, which is what makes its window-edge gestures -
// "create split view" and the like - offer themselves when a drag pauses near the edge.
// Only our own drop handlers read this payload, so nothing needs the standard types.
const DND_TYPE = "application/x-camur-tree-item";

export default {
  name: "ProjectTree",
  components: {
    ProjectTreeItem,
    ProjectTreeFilter,
    ProjectTreeIndicatorArrow,
    ProjectTreeBtn,
    TreeOpenCloseBtn
  },
  props: {
    // Set by ProjectBaseView, which owns the resize handle beside the tree
    width: {
      type: Number,
      default: 275
    }
  },
  data() {
    return {
      filter_open: true,
      filter_text: "",
      display_arrow: false,
      dragged_node: null,
      hovered_area: null,

      new_zc_id: 0,
      new_zone_modal: false,

      new_ctrl_modal: false,
      new_node_modal: false,
      new_node_type: 14,
      new_node_prj_id: 0,
      new_node_zone_id: 0,
      new_item_name: "",

      // Holds what the tree currently is, not what the next click will do. The items
      // default to tree_open: true, so starting this at false made the first click
      // expand an already expanded tree - only flipping the icon.
      expand_or_collapse: true,
      selectedCapability: null,
      draggedControllerId: null,
      draggedZoneId: null,
      draggedNodeId: null,
      isDragging: false,
    };
  },

  created() {
    this.fetch_required_data();
  },

  mounted() {
    this.initiate_cntrls_sorting();
    this.initiate_zones_sorting();
    this.initiate_nodes_sorting();

    window.addEventListener('drop', this.onGlobalDrop);      // <— no “true”
    window.addEventListener('dragend', this.onGlobalDragEnd);
  },
  beforeUnmount() {
    window.removeEventListener('drop', this.onGlobalDrop);
    window.removeEventListener('dragend', this.onGlobalDragEnd);
  },

  watch: {
    // cntrlsLength:   { immediate: true, handler() { this.initiate_cntrls_sorting(); } },
    // zonesLength:    { immediate: true, handler() { this.initiate_zones_sorting(); } },
    // allNodesLength: { immediate: true, handler() { this.initiate_nodes_sorting(); } },
  },

  methods: {
    clearDragState() {
    this.draggedControllerId = null;
    this.draggedZoneId = null;
    this.draggedNodeId = null;
    this.isDragging = false;
    this.$app.dragged_item = null;

    if (this._hoverEl) {
      this._hoverEl.classList.remove('tree-item-border-top','tree-item-border-bottom');
      this._hoverEl = null;
      this._hoverHalf = null;
    }
  },
    onGlobalDrop() {
      this.clearDragState();
    },
    onGlobalDragEnd() {
      this.clearDragState();
    },
    onProjectDrop(e) {
      this.isDragging = false;
      if ((this.dragged_type === 'virtual_controller' && this.$app.dragged_item?.virtual) || this.dragged_type ==="cntrldevice") {
        this.new_controller_from_device(this.$app.sltd.prj_id);
      }
      this.clearDragState();
    },
    updateDropEffect(e) {
      const el = e.target?.closest?.('.drop-enabled, .drop-disabled');
      if (!el) {
        e.dataTransfer.dropEffect = 'none';
        if (this._hoverEl) { this._hoverEl.classList.remove('tree-item-border-top','tree-item-border-bottom'); this._hoverEl=null; this._hoverHalf=null; }
        return;
      }

      // Respect enabled/disabled
      e.dataTransfer.dropEffect = el.classList.contains('drop-enabled') ? 'move' : 'none';

      const kind = el.dataset.kind; // 'node' | 'zone' | 'controller'
      const draggingNode       = !!this.dragged_node_id;
      const draggingZone       = !!this.dragged_zone_id && !draggingNode;
      const draggingController = !!this.dragged_cntrl_id && !draggingNode && !draggingZone;

      const dragged =
        draggingNode       ? this.dragged_node_id  :
        draggingZone       ? this.dragged_zone_id  :
        draggingController ? this.dragged_cntrl_id : null;
      const over_dragged_row =
        dragged != null && String(el.dataset.id) === String(dragged.id);

      // Only show the bar when dragging the same kind
      const showForThisEl =
        !over_dragged_row && (
          (kind === 'node'       && draggingNode) ||
          (kind === 'zone'       && draggingZone) ||
          (kind === 'controller' && draggingController)
        );

      if (!showForThisEl || !el.classList.contains('drop-enabled')) {
        if (this._hoverEl) { this._hoverEl.classList.remove('tree-item-border-top','tree-item-border-bottom'); this._hoverEl=null; this._hoverHalf=null; }
        return;
      }

      const rect = el.getBoundingClientRect?.(); if (!rect) return;
      const half = (e.clientY <= rect.top + rect.height / 2) ? 'top' : 'bottom';

      if (this._hoverEl !== el || this._hoverHalf !== half) {
        if (this._hoverEl) this._hoverEl.classList.remove('tree-item-border-top','tree-item-border-bottom');
        this._hoverEl = el; this._hoverHalf = half;
        el.classList.toggle('tree-item-border-top', half === 'top');
        el.classList.toggle('tree-item-border-bottom', half === 'bottom');
      }
    },

    async resetAllSortIndexes() {
      return;
      const projectId = this.$app.sltd.project.id;

      // 1) Reset controllers
      const ctrlUpdates = this.sorted_cntrls.map(ctrl => {
        ctrl.locals = ctrl.locals || {};
        ctrl.locals.sortIndex = 0;
        ctrl.dto.sortIndex   = 0;
        return this.$net.cntrl.updateSortIndex(projectId, ctrl.id, 0);
      });

      // 2) Reset zones
      const zoneUpdates = [];
      this.sorted_cntrls.forEach(ctrl => {
        this.zones_for_controller(ctrl).forEach(zone => {
          zone.locals = zone.locals || {};
          zone.locals.sortIndex = 0;
          zone.dto.sortIndex   = 0;
          zoneUpdates.push(
            this.$net.zone.updateSortIndex(projectId, zone.id, 0)
          );
        });
      });

      // 3) Reset nodes
      const nodeUpdates = [];
      // iterate every zone in the project
      this.$app.sltd.project.zones.forEach(zone => {
        this.sorted_nodes_for_zone(zone).forEach(node => {
          node.locals = node.locals || {};
          node.locals.sortIndex = 0;
          node.dto.sortIndex   = 0;
          nodeUpdates.push(
            this.$net.node.updateSortIndex(
              projectId,
              zone.id,
              node.id,
              0
            )
          );
        });
      });

      // fire them all in parallel
      await Promise.all([...ctrlUpdates, ...zoneUpdates, ...nodeUpdates]);

      console.log('✅ All sortIndex values reset to 0 and saved');
    },

    parseDnD(e) {
      const t = e.dataTransfer?.getData(DND_TYPE) || '';
      if (!t) return { kind: '', id: null };
      if (t.includes(':')) {
        const [kind, id] = t.split(':');
        return { kind, id: String(id) };
      }
      // legacy fallback
      return { kind: '', id: String(t) };
    },

    onControllerDragStart(e, ctrl) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData(DND_TYPE, `controller:${ctrl.id}`);
      this.$app.dragged_item = null;
      this.draggedControllerId = ctrl;
      this.isDragging = true;
    },
    onZoneDragStart(e, zone) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData(DND_TYPE, `zone:${zone.id}`);
      this.$app.dragged_item = null;
      this.draggedZoneId = zone;
      this.isDragging = true;
    },
    onNodeDragStart(e, node) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData(DND_TYPE, `node:${node.id}`);
      this.$app.dragged_item = null;
      this.draggedNodeId = node;
      this.isDragging = true;
    },
    
    onDragEnd() {
      this.clearDragState();
    },

    async initiate_cntrls_sorting() {
      const project = this.$app?.sltd?.project;
      if (!project) return;

      const cntrls = (project.cntrls || []).slice();

      // Stabil sortering: primärt på sortIndex, sekundärt på namn
      cntrls.sort((a, b) => {
        const ai = Number.isInteger(a?.dto?.sortIndex) ? a.dto.sortIndex : Number.MAX_SAFE_INTEGER;
        const bi = Number.isInteger(b?.dto?.sortIndex) ? b.dto.sortIndex : Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return (a?.name || '').localeCompare(b?.name || '');
      });

      const updates = [];
      for (let i = 0; i < cntrls.length; i++) {
        const desired = i + 1; // 1..n
        const c = cntrls[i];

        // sätt locals för reaktiv visning
        c.locals = c.locals || {};
        if (c.locals.sortIndex !== desired) c.locals.sortIndex = desired;

        // skriv bara om dto skiljer sig
        const current = c.dto?.sortIndex ?? 0;
        if (current !== desired) {
          c.dto.sortIndex = desired; // håll modellen i synk
          updates.push(this.$net.cntrl.updateSortIndex(project.id, c.id, desired));
        }
      }

      if (updates.length) await Promise.all(updates);
    },

    async initiate_zones_sorting() {
      const projectId = this.$app?.sltd?.project?.id;
      if (!projectId) return;

      // Kör per controller
      for (const cntrl of (this.$app.sltd.project?.cntrls || [])) {
        // Ta zonerna för controller och sortera stabilt
        const zones = this.zones_for_controller(cntrl).slice().sort((a, b) => {
          const ai = Number.isInteger(a?.dto?.sortIndex) ? a.dto.sortIndex : Number.MAX_SAFE_INTEGER;
          const bi = Number.isInteger(b?.dto?.sortIndex) ? b.dto.sortIndex : Number.MAX_SAFE_INTEGER;
          if (ai !== bi) return ai - bi;
          return (a?.name || '').localeCompare(b?.name || '');
        });

        const updates = [];
        for (let i = 0; i < zones.length; i++) {
          const desired = i + 1; // 1..n
          const z = zones[i];

          // håll UI reaktivt
          z.locals = z.locals || {};
          if (z.locals.sortIndex !== desired) z.locals.sortIndex = desired;

          // skriv bara om dto skiljer sig
          const current = z.dto?.sortIndex ?? 0;
          if (current !== desired) {
            z.dto.sortIndex = desired; // synka modellen lokalt
            updates.push(this.$net.zone.updateSortIndex(projectId, z.id, desired));
          }
        }
        if (updates.length) await Promise.all(updates);
      }
    },


    async initiate_nodes_sorting() {
      const project = this.$app?.sltd?.project;
      if (!project) return;

      const projectId = project.id;

      // per controller
      for (const cntrl of (project.cntrls || [])) {
        // per zon under controller
        for (const zone of this.zones_for_controller(cntrl)) {
          // jobba på den faktiska listan; ta bara noder med serialNumber
          const nodes = (zone.nodes || [])
            .filter(nd => nd?.serialNumber) // behåll din regel
            .slice()
            .sort((a, b) => {
              const ai = Number.isInteger(a?.dto?.sortIndex) ? a.dto.sortIndex : Number.MAX_SAFE_INTEGER;
              const bi = Number.isInteger(b?.dto?.sortIndex) ? b.dto.sortIndex : Number.MAX_SAFE_INTEGER;
              if (ai !== bi) return ai - bi;
              return (a?.name || '').localeCompare(b?.name || '');
            });

          const updates = [];
          for (let i = 0; i < nodes.length; i++) {
            const desired = i + 1; // 1..n
            const nd = nodes[i];

            // håll UI reaktivt
            nd.locals = nd.locals || {};
            if (nd.locals.sortIndex !== desired) nd.locals.sortIndex = desired;

            // skriv bara om dto skiljer sig
            const current = nd.dto?.sortIndex ?? 0;
            if (current !== desired) {
              nd.dto.sortIndex = desired; // synka modellen lokalt
              updates.push(
                this.$net.node.updateSortIndex(projectId, zone.id, nd.id, desired)
              );
            }
          }
          if (updates.length) await Promise.all(updates);
        }
      }
    },


    cntrl_of_node(node) {
      if (!node) {
        return;
      }
      if (!node.dto) {
        return;
      }
      for (let cntrl of this.$app.sltd.project.cntrls) {
        if (node.dto.controllerId === cntrl.id) {
          return cntrl;
        } else if (node.dto.controllerSerialNumber === cntrl.serial) {
          return cntrl;
        }
      }
    },
    lockProject() {
      // console.log("lockProject");
      this.$app.sltd.project.app_prop.edit_lock = !this.$app.sltd.project.app_prop.edit_lock;
      this.$appm.saveProjectLock(this.$app.sltd.project.id, this.$app.sltd.project.app_prop.edit_lock);
      // console.log(this.$app.sltd.project.app_prop.edit_lock ? "true":"false");
    },
    include_item(item) {
      if (
        this.$route.name === 'config_multiple_nodes' && 
        item.item_type === "node"
      ) {
        //check if p4 and p4i
        // Check if the selected capability is different from the item's capability
        if (!this.matchingCapability(item)) {
          // Deselect all nodes if the capabilities are different
          for (let node of this.$app.sltd.project.all_nodes || []) {
            if (
              node && 
              node.app_prop
            ) {
              node.app_prop.include_select = false;
            }
          }
          this.$app.sltd.project.app_prop.include_select = false;
          for (let cntrl of this.$app.sltd.project.cntrls || []) {
            if (
              cntrl && 
              cntrl.app_prop
            ) {
              cntrl.app_prop.include_select = false;
            }
            for (let zone of cntrl.zones || []) {
              if (
                zone && 
                zone.app_prop
              ) {
                zone.app_prop.include_select = false;
              }
            }
          }

          // Update the selected capability
          this.selectedCapability = item.capability;
        }
        // Select the current node
        // item.app_prop.include_select = !item.app_prop.include_select;
      }
      
      if (this.$route.name == 'config_multiple_nodes') {
        item.app_prop.include_select = !item.app_prop.include_select;
        let anySelected = false;
        
        for (let node of this.$app.sltd.project.all_nodes) {
          if (node.app_prop.include_select == true) {
            anySelected = true;
          }
        }
        if (anySelected == false) {
          this.selectedCapability = null;
        }
      }
      // console.log(this.selectedCapability)
      
      if (item.item_type == "zone") {
        let anySelectedNodesRecording = false;
        for (let node of item.nodes) {
          if (this.$route.name === 'config_multiple_nodes') {
            if (this.matchingCapability(node)) {
              for (let node_ of this.$app.sltd.project.all_nodes) {
                if (node_.app_prop.include_select) {
                  if (this.cntrl_of_node(node_).dto.recordingMode > this.$camur.recording_type.rectype_monitor) {
                    anySelectedNodesRecording = true;
                    break;
                  }
                }
              }
              if ((this.cntrl_of_node(node).dto.recordingMode > this.$camur.recording_type.rectype_monitor) === anySelectedNodesRecording) {
                node.app_prop.include_select = item.app_prop.include_select;
                if (node.serial && !node.connected) {
                  node.app_prop.include_select = false;
                } 
                
              }
            }
          } else {
            // Require assigned for project live values
            if (
              (node.serial > 0) || 
              (this.$ui.select_mode_level == this.$camur.select_mode_level.controller)
            ) {
              node.app_prop.include_select = item.app_prop.include_select;
            }
          }
          //console.log(node);
        }
      }
      else if (item.item_type == "cntrl") {
        let zones = this.zones_for_controller(item)
        for (let zone of zones) {
          zone.app_prop.include_select = item.app_prop.include_select;
          if (this.$route.name === 'config_multiple_nodes') {
            for (let node of zone.nodes) {
              if (this.matchingCapability(node)) {
                if (node.serial && !node.connected) {
                  node.app_prop.include_select = false;
                } else {
                  node.app_prop.include_select = item.app_prop.include_select;
                }
              }
            }
          }
          //console.log(zone);
        }
        for (let node of item.nodes) {
          if (this.$route.name === 'config_multiple_nodes') {
            if (this.matchingCapability(node)) {
              let anySelectedNodesRecording = false;
              for (let node_ of this.$app.sltd.project.all_nodes) {
                if (node_.app_prop.include_select) {
                  if (this.cntrl_of_node(node_).dto.recordingMode > this.$camur.recording_type.rectype_monitor) {
                    anySelectedNodesRecording = true;
                    break;
                  }
                }
              }
              if ((this.cntrl_of_node(node).dto.recordingMode > this.$camur.recording_type.rectype_monitor) === anySelectedNodesRecording) {
                if (node.serial && !node.connected) {
                  node.app_prop.include_select = false;
                } else {
                  node.app_prop.include_select = item.app_prop.include_select;
                }
              }
            }
          } else {          
              // Require assigned for project live values
            if (
              (node.serial > 0) || 
              (this.$ui.select_mode_level == this.$camur.select_mode_level.controller)
            ) {
              node.app_prop.include_select = item.app_prop.include_select;
            }
          }
          //console.log(node);
        }
      }
      else if (item.item_type == "project") {
        for (let controller of item.cntrls) {
          controller.app_prop.include_select = item.app_prop.include_select;

          for (let node of controller.nodes) {
            if (this.$route.name === 'config_multiple_nodes' && !(controller.dto.recordingMode > this.$camur.recording_type.rectype_monitor)) {
              if (this.matchingCapability(node)) {
                node.app_prop.include_select = item.app_prop.include_select;
              }
            }
          }
        }
        for (let zone of item.zones) {
          zone.app_prop.include_select = item.app_prop.include_select;
          for (let node of zone.nodes) {
            if (this.$route.name === 'config_multiple_nodes') {
              if (this.matchingCapability(node)) {
                let anySelectedNodesRecording = false;
                for (let node_ of this.$app.sltd.project.all_nodes) {
                  if (node_.app_prop.include_select) {
                    if (this.cntrl_of_node(node_).dto.recordingMode > this.$camur.recording_type.rectype_monitor) {
                      anySelectedNodesRecording = true;
                      break;
                    }
                  }
                }
                if ((this.cntrl_of_node(node).dto.recordingMode > this.$camur.recording_type.rectype_monitor) === anySelectedNodesRecording) {
                  if (node.serial && !node.connected) {
                    node.app_prop.include_select = false;
                  } else {
                    node.app_prop.include_select = item.app_prop.include_select;
                  }
                }
              }
            } else {
              // Require assigned for project live values
              if (
                (node.serial > 0) || 
                (this.$ui.select_mode_level == this.$camur.select_mode_level.controller)
              ) {
                node.app_prop.include_select = item.app_prop.include_select;
              }
            }
            //console.log(node);
          }
        }
      }
      let anyNodeSelected = false;
      let allNodesWithSameCapabilityInProjectSelected = true;
      let allNodesWithSameCapabilityInCntrlSelected = true;
      let allNodesWithSameCapabilityInZoneSelected = true;
      let hasNodesWithSelectedCapability = false;
      let anySelectedNodesRecording = false;
      for (let node_ of this.$app.sltd.project.all_nodes) {
        if (node_.app_prop.include_select) {
          if (this.cntrl_of_node(node_).dto.recordingMode > this.$camur.recording_type.rectype_monitor) {
            anySelectedNodesRecording = true;
            break;
          }
        }
      }

      if (this.$route.name === 'config_multiple_nodes') {
        // Check if any node in the project is selected
        for (let node of this.$app.sltd.project.all_nodes) {
          if (node.app_prop.include_select) {
            anyNodeSelected = true;
            break;
          }
        }

        // Check if all nodes with the same capability in the project are selected
        for (let node of this.$app.sltd.project.all_nodes) {
          if (this.matchingCapability(node)) {
            hasNodesWithSelectedCapability = true;
            if (
              !(node.serial && !node.connected) &&
              !node.app_prop.include_select && 
              (this.cntrl_of_node(node).dto.recordingMode > this.$camur.recording_type.rectype_monitor) === anySelectedNodesRecording
            ) {
              allNodesWithSameCapabilityInProjectSelected = false;
            }
          }
        }

        if (
          !anyNodeSelected || 
          !allNodesWithSameCapabilityInProjectSelected
        ) {
          this.$app.sltd.project.app_prop.include_select = false;
        } else {
          this.$app.sltd.project.app_prop.include_select = true;
        }

        // Check if all nodes with the same capability in each controller are selected
        for (let cntrl of this.$app.sltd.project.cntrls) {
          allNodesWithSameCapabilityInCntrlSelected = true;
          hasNodesWithSelectedCapability = false;
          for (let node of cntrl.nodes || []) {
            if (this.matchingCapability(node) && !(node.serial && !node.connected)) {
              hasNodesWithSelectedCapability = true;
              if (!node.app_prop.include_select) {
                allNodesWithSameCapabilityInCntrlSelected = false;
              }
            }
          }
          if (!hasNodesWithSelectedCapability) {
            allNodesWithSameCapabilityInCntrlSelected = false;
          }
          cntrl.app_prop.include_select = allNodesWithSameCapabilityInCntrlSelected;
        }

        // Check if all nodes with the same capability in each zone are selected
        for (let zone of this.$app.sltd.project.zones) {
          allNodesWithSameCapabilityInZoneSelected = true;
          hasNodesWithSelectedCapability = false;
          for (let node of zone.nodes || []) {
            if (this.matchingCapability(node) && !(node.serial && !node.connected)) {
              hasNodesWithSelectedCapability = true;
              if (!node.app_prop.include_select) {
                allNodesWithSameCapabilityInZoneSelected = false;
              }
            }
          }
          if (!hasNodesWithSelectedCapability) {
            allNodesWithSameCapabilityInZoneSelected = false;
          }
          zone.app_prop.include_select = allNodesWithSameCapabilityInZoneSelected;
        }
      }
    },
    toggle_zone_open(zone, open) {
      zone.app_prop.tree_open = open;
    },

    new_zone(ctrl) {
      this.new_zc_id = ctrl.id;
      this.new_zone_modal = true;
    },

    new_node(zone_id, prj_id) {
      this.new_node_zone_id = zone_id;
      this.new_node_prj_id = prj_id;
      this.new_node_modal = true;
    },

    new_node_from_drop(zone_id, prj_id) {
      let node_data = {};

      if (this.$app.dragged_item.virtual) {
        node_data = {
          name: this.$app.dragged_item.name,
          capability: this.$app.dragged_item.type,
          gridX : 0,
          gridY : 0
          // serialNumber: 0
        };
      } else {
        if (!this.$util.is_node_device(this.$app.dragged_item)) {
          return;
        }

        let name = `${this.$appm.get_node_type_name(
          this.$app.dragged_item.dto.capability
        )}`;

        node_data = {
          name: name,
          capability: this.$app.dragged_item.dto.capability,
          serialNumber: this.$app.dragged_item.dto.serialNumber,
          gridX : gp.x,
          gridY : gp.y
        };
      }

      this.$net.node.create(prj_id, zone_id, node_data);
    },

    new_zone_from_drop(prj_id, cntrl_id) {
      let data = {
        name: this.$app.dragged_item.name,
        controllerId: cntrl_id
      };
      this.$net.zone.create(prj_id, data);
    },

    new_controller() {
      this.new_ctrl_modal = true;
      this.new_item_name = this.next_controller_name();
    },

    next_controller_name() {
      return `${this.$txt.cntrl} ${this.$app.sltd.project.children.cntrls
        .length + 1}`;
    },

    new_controller_from_device(prj_id) {
      let data = { name: this.next_controller_name() };

      if (this.$app.dragged_item.virtual) {
        data.serialNumber = 0;
      } else {
        if (!this.$util.is_cntrl_device(this.$app.dragged_item)) {
          return;
        }
        data.serialNumber = this.$app.dragged_item.serial;
      }
      this.$net.cntrl.post(prj_id, data);
    },

    create_zone: function() {
      let zone_data = {
        name: this.new_item_name,
        controllerId: parseInt(this.new_zc_id)
      };
      this.$net.zone.create(this.$app.sltd.project.id, zone_data);
    },

    create_ctrl: function(prj_id) {
      let ctrl_data = {
        name: this.new_item_name
      };
      this.$net.cntrl.post(prj_id, ctrl_data);
    },

    create_node() {
      let node_data = {
        name: this.new_item_name,
        capability: this.new_node_type,
        gridX : 0,
        gridY : 0
      };

      this.$net.node.create(
        this.new_node_prj_id,
        this.new_node_zone_id,
        node_data
      );
    },

    get_open(zone) {
      return zone.app_prop.tree_open;
    },

    fetch_required_data() {
      let prj_id = this.$app.sltd.prj_id;
      if (prj_id) {
        this.$net.project.fetch(prj_id);
        this.$net.cntrl.list(prj_id);
        this.$net.cntrl_device.list(prj_id);
        this.$net.zone.list(prj_id);
        this.$net.graph_props.list(prj_id);
      }
      this.ensure_nodes_loaded(0);
    },

    ensure_nodes_loaded(attempt) {
      let prj_id = this.$app.sltd.prj_id;
      if (prj_id) {
        let project = this.$app.sltd.project;
        if (project && project.zones.length > 0) {
          for (let zone of project.zones) {
            this.$net.node.list(prj_id, zone.id);
          }
          return;
        }
      }
      if (attempt < 25) {
        setTimeout(() => this.ensure_nodes_loaded(attempt + 1), 1000);
      }
    },

    zone_has_nodes(z) {
      return z != null && z.nodes.length > 0;
    },

    zones_for_controller(controller) {
      const zones = controller.zones

      const hasIdx = z => Number(z?.locals?.sortIndex ?? 0) > 0;

      const withIndex    = zones.filter(hasIdx);
      const withoutIndex = zones.filter(z => !hasIdx(z));

      const head = this.$util.sort_table(withIndex, 'sortIndex');
      const tail = this.$util.sort_table(withoutIndex, 'name');

      return [...head, ...tail];
    },

    match_filter: function(item) {
      var name_match = this.$util.search_matches(item.name, this.filter_text);

      var type_match = false;
      if (item.type != "zone") {
        var type_name = this.$appm.get_node_type_name(item.type);
        type_match = this.$util.search_matches(type_name, this.filter_text);
      }

      var serial_match = false;
      if (item.type != "node") {
        serial_match = this.$util.search_matches(
          String(item.serial),
          this.filter_text
        );
      }

      var status_match = false;
      if (this.$util.search_matches(item.status, this.filter_text)) {
        status_match = true;
      }

      return name_match || type_match || serial_match || status_match;
    },

    is_selected: function(item) {
      if (
        this.$util.is_node(item) && 
        this.$app.sltd.node != null
      ) {
        return item.id == this.$app.sltd.node.id;
      } else if (
        this.$util.is_zone(item) && 
        this.$app.sltd.zone != null
      ) {
        return item.id == this.$app.sltd.zone.id;
      } else if (
        this.$util.is_project(item) &&
        this.$app.sltd.project != null
      ) {
        return item.id == this.$app.sltd.project.id;
      } else if (
        this.$util.is_controller(item) &&
        this.$app.sltd.project != null
      ) {
        return item.id == this.$app.sltd.controller_id;
      } else return false;
    },

    is_secondary_selected(item) {
      if (this.$util.is_zone(item)) {
        return this.is_selected(item) && this.$app.sltd.node != null;
      } else if (this.$util.is_project(item)) {
        return this.is_selected(item) && this.$app.sltd.controller_id != null;
      } else if (this.$util.is_controller(item)) {
        return this.is_selected(item) && this.$app.sltd.zone != null;
      } else {
        return false;
      }
    },

    sorted_nodes_for_zone(zone) {
      const nodes = zone.nodes || [];
      const hasIdx = n => Number(n?.locals?.sortIndex ?? 0) > 0;

      const withIndex    = this.$util.sort_table(nodes.filter(hasIdx), 'sortIndex');
      let withoutIndex = nodes.filter(n => !hasIdx(n));
      if (withoutIndex.length) {
        withoutIndex = this.$util.sort_table(withoutIndex, 'name'); // secondary
        withoutIndex = this.$util.sort_table(withoutIndex, 'type'); // primary
      }
      return [...withIndex, ...withoutIndex];
    },

    // matchingCapability(node) {
    //     if (
    //       node.capability === this.selectedCapability || 
    //       node.capability === this.$camur.node_type.ntype_p4 && 
    //       this.selectedCapability === this.$camur.node_type.ntype_p4i || 
    //       node.capability === this.$camur.node_type.ntype_p4i && 
    //       this.selectedCapability === this.$camur.node_type.ntype_p4
    //     ) {
    //       return true;
    //   } else {
    //       return false;
    //   }
    // },
    matchingCapability(node) {
      const fvTypes = [this.$camur.node_type.ntype_fv01, this.$camur.node_type.ntype_fv1a, this.$camur.node_type.ntype_fv03];

      if (
        node.capability === this.selectedCapability ||
        (node.capability === this.$camur.node_type.ntype_p4 &&
        this.selectedCapability === this.$camur.node_type.ntype_p4i) ||
        (node.capability === this.$camur.node_type.ntype_p4i &&
        this.selectedCapability === this.$camur.node_type.ntype_p4)
      ) {
        return true;
      }

      if (node.capability === this.selectedCapability || (fvTypes.includes(node.capability) && fvTypes.includes(this.selectedCapability))) {
        return true;
      }

      return false;
    },
    async onControllerDrop(e, targetCtrl) {
      this.isDragging = false;

      // A) virtual zone → create a zone under this controller
      if (this.dragged_type === 'virtual_zone' && this.$app.dragged_item?.virtual) {
        this.new_zone_from_drop(this.$app.sltd.prj_id, targetCtrl.id);
        return this.clearDragState();
      }

      // B) typed payload parsing (controller reordering only)
      const { kind, id } = this.parseDnD(e);

      if (kind === 'controller') {
        if (String(targetCtrl.id) === id) return this.clearDragState();

        const li   = e.currentTarget;
        const rect = li?.getBoundingClientRect?.();
        const after = rect ? (e.clientY > rect.top + rect.height / 2) : false;

        const list = this.sorted_cntrls.slice();
        const draggedIdx = list.findIndex(c => String(c.id) === id);
        const targetIdx  = list.findIndex(c => c.id === targetCtrl.id);
        if (draggedIdx < 0 || targetIdx < 0) return this.clearDragState();

        const [dragged] = list.splice(draggedIdx, 1);
        let insertIndex = targetIdx;
        if (draggedIdx < targetIdx) insertIndex -= 1;
        if (after) insertIndex += 1;
        list.splice(insertIndex, 0, dragged);

        await Promise.all(
          list.map((c, i) => {
            const newIndex = i + 1;
            if ((c.locals?.sortIndex ?? c.dto.sortIndex) === newIndex) return Promise.resolve();
            c.locals = c.locals || {};
            c.locals.sortIndex = newIndex;
            c.dto.sortIndex    = newIndex;
            return this.$net.cntrl.updateSortIndex(this.$app.sltd.project.id, c.id, newIndex);
          })
        );

        return this.clearDragState();
      }

      // C) ignore other kinds on controller rows
      return this.clearDragState();
    },


    async onZoneDrop(e, targetZone) {
      this.isDragging = false;

      if (this.dragged_type === 'virtual_node' && this.$app.dragged_item?.virtual) {
        this.new_node_from_drop(targetZone.id, this.$app.sltd.prj_id);
        return this.clearDragState();
      }

      const { kind, id } = this.parseDnD(e);
      if (!id) return this.clearDragState();

      if (kind === 'node') {
        const draggedNode = this.$app.sltd.project.all_nodes?.find(n => String(n.id) === id);
        if (!draggedNode) return this.clearDragState();

        const fromZoneId = draggedNode.locals?.zoneId ?? draggedNode.dto.zoneId;
        const fromZone   = this.$app.sltd.project.zones.find(z => z.id === fromZoneId);
        if (!fromZone || !targetZone) return this.clearDragState();

        const nodeCtrl = this.cntrl_of_node(draggedNode)?.id;
        const zoneCtrl = this.cntrl_of_zone(targetZone)?.id;
        if (nodeCtrl !== zoneCtrl) return this.clearDragState();

        // ask before cross-zone move (same controller or not)
        if (fromZone.id !== targetZone.id) {
          const ok = confirm(`${window.app.$txt.move_node}\n ${draggedNode.name} → ${targetZone.name}`);
          if (!ok) return this.clearDragState();
        }

        // optional: drop to top/bottom by cursor half
        const rect = e.currentTarget?.getBoundingClientRect?.();
        const toTop = rect ? (e.clientY <= rect.top + rect.height / 2) : false;

        // compute desired position before any mutations
        const targetArr = targetZone.nodes || [];
        const insertAt  = toTop ? 0 : targetArr.length;

        // pre-set desired sort index so change_zone sends {zoneId, sortIndex} atomically
        draggedNode.locals = draggedNode.locals || {};
        draggedNode.locals.sortIndex = insertAt + 1;

        // persist zone change to server (response includes correct sortIndex, no visual jump)
        await draggedNode.change_zone(targetZone.id);

        // compute desired sort order BEFORE splicing (node is still in fromZone)
        const desiredSource = (this.sorted_nodes_for_zone(fromZone) || []).filter(n => n.id !== draggedNode.id);
        const desiredTarget = (this.sorted_nodes_for_zone(targetZone) || []).slice();
        desiredTarget.splice(insertAt, 0, draggedNode);

        // now move in arrays
        const fromArr = fromZone.nodes || [];
        const srcIdx  = fromArr.findIndex(n => n.id === draggedNode.id);
        if (srcIdx > -1) fromArr.splice(srcIdx, 1);
        targetArr.splice(insertAt, 0, draggedNode);

        await this.reindexAndPersistNodes(fromZone,  desiredSource);
        await this.reindexAndPersistNodes(targetZone, desiredTarget);

        return this.clearDragState();
      }

      // B) Zone dropped on a zone → reorder zones (unchanged logic)
      if (kind === 'zone') {
        // Dropped on itself - see onControllerDrop
        if (String(targetZone.id) === id) return this.clearDragState();

        const li   = e.currentTarget;
        const rect = li?.getBoundingClientRect?.();
        const after = rect ? (e.clientY > rect.top + rect.height / 2) : false;

        const allZones  = this.zones_for_controller(this.cntrl_of_zone(targetZone)).slice();
        const draggedIdx = allZones.findIndex(z => String(z.id) === id);
        const targetIdx  = allZones.findIndex(z => z.id === targetZone.id);
        if (draggedIdx < 0 || targetIdx < 0) return this.clearDragState();

        const [dragged] = allZones.splice(draggedIdx, 1);
        let insertIndex = targetIdx;
        if (draggedIdx < targetIdx) insertIndex -= 1;
        if (after) insertIndex += 1;
        allZones.splice(insertIndex, 0, dragged);

        await Promise.all(
          allZones.map((z, i) => {
            const newIndex = i + 1;
            if ((z.locals?.sortIndex ?? z.dto.sortIndex) === newIndex) return Promise.resolve();
            z.locals = z.locals || {};
            z.locals.sortIndex = newIndex;
            z.dto.sortIndex    = newIndex;
            return this.$net.zone.updateSortIndex(this.$app.sltd.project.id, z.id, newIndex);
          })
        );

        return this.clearDragState();
      }

      // otherwise ignore
      this.clearDragState();
    },


    cntrl_of_zone(zone) {
      const ctrlId = zone?.dto?.controllerId ?? zone?.controllerId;
      return this.$app.sltd.project.cntrls.find(c => c.id === ctrlId);
    },

    async reindexAndPersistNodes(zone, list) {
      // Reassign 1..n and persist only when changed
      await Promise.all(
        list.map((n, i) => {
          const newIdx = i + 1;
          const oldIdx = n.locals?.sortIndex ?? n.dto.sortIndex;
          if (oldIdx === newIdx) return Promise.resolve();
          n.locals = n.locals || {};
          n.locals.sortIndex = newIdx;
          n.dto.sortIndex    = newIdx;
          return this.$net.node.updateSortIndex(
            this.$app.sltd.project.id,
            zone.id,
            n.id,
            newIdx
          );
        })
      );
    },

    async onNodeDrop(e, targetNode, targetZone) {
      this.isDragging = false;
      e.preventDefault();
      e.stopPropagation();

      const { kind, id } = this.parseDnD(e);
      if (kind !== 'node') return this.clearDragState();

      const draggedId = id;
      if (!draggedId) return;

      const li   = e.currentTarget;
      const rect = li?.getBoundingClientRect?.();
      const after = rect ? (e.clientY > rect.top + rect.height / 2) : false;

      const draggedNode = this.$app.sltd.project.all_nodes?.find(n => String(n.id) === draggedId);
      if (!draggedNode) return;

      if (targetNode.id === draggedNode.id) return this.clearDragState();

      const fromZoneId = draggedNode.locals?.zoneId ?? draggedNode.dto.zoneId;
      const fromZone   = this.$app.sltd.project.zones.find(z => z.id === fromZoneId);
      if (!targetZone || !fromZone) return;

      const displayedSource = this.sorted_nodes_for_zone(fromZone).slice();
      const displayedTarget = this.sorted_nodes_for_zone(targetZone).slice();

      if (fromZone.id === targetZone.id) {
        const list = displayedTarget.slice();
        const from = list.findIndex(n => String(n.id) === draggedId);
        let to     = list.findIndex(n => n.id === targetNode.id);
        if (from < 0 || to < 0) return;

        const [dragged] = list.splice(from, 1);
        if (from < to) to -= 1;

        const insertIndex = after ? to + 1 : to;
        list.splice(insertIndex, 0, dragged);

        await this.reindexAndPersistNodes(targetZone, list);
        return;
      }

      const nodeCtrl = this.cntrl_of_node(draggedNode)?.id;
      const zoneCtrl = this.cntrl_of_zone(targetZone)?.id;
      if (nodeCtrl !== zoneCtrl) return;

      let to = displayedTarget.findIndex(n => n.id === targetNode.id);
      if (to < 0) return;

      const ok = confirm(`${window.app.$txt.move_node}\n ${draggedNode.name} → ${targetZone.name}`);
      if (!ok) return;

      const insertIndexInDisplay = after ? to + 1 : to;

      // pre-set desired sort index so change_zone sends {zoneId, sortIndex} atomically
      draggedNode.locals = draggedNode.locals || {};
      draggedNode.locals.sortIndex = insertIndexInDisplay + 1;

      // persist zone change to server (response includes correct sortIndex, no visual jump)
      await draggedNode.change_zone(targetZone.id);

      // now move in arrays (server has confirmed)
      const fromArr   = fromZone.nodes || [];
      const targetArr = targetZone.nodes || [];

      const fromIdx = fromArr.findIndex(n => n.id === draggedNode.id);
      if (fromIdx > -1) fromArr.splice(fromIdx, 1);

      const clamped = Math.min(Math.max(insertIndexInDisplay, 0), targetArr.length);
      targetArr.splice(clamped, 0, draggedNode);

      const desiredSourceList = displayedSource.filter(n => n.id !== draggedNode.id);

      const desiredTargetList = displayedTarget.slice();
      desiredTargetList.splice(insertIndexInDisplay, 0, draggedNode);

      await this.reindexAndPersistNodes(fromZone,  desiredSourceList);
      await this.reindexAndPersistNodes(targetZone, desiredTargetList);
      this.clearDragState();
    },



    submit_sort_index(items_list) {
      for (let item of items_list) {
        if (item.locals.sortIndex !== item.dto.sortIndex) {
          if (item.item_type == "cntrl") {
            this.$net.cntrl.updateSortIndex(
              this.$app.sltd.project.id,
              item.id,
              item.locals.sortIndex,
              () => item.fetch && item.fetch()
            );
          }
          if (item.item_type == "zone") {

          }
        }
      }
    }
  },

  computed: {
    drag_origin_controller_id() {
      if (this.draggedZoneId) {
        return this.draggedZoneId?.dto?.controllerId ?? this.draggedZoneId?.controllerId ?? null;
      }
      if (this.draggedNodeId) {
        return this.cntrl_of_node(this.draggedNodeId)?.id ?? null;
      }
      return null;
    },
    cntrlsLength()   { return this.$app.sltd.project?.cntrls?.length     || 0; },
    zonesLength()    { return this.$app.sltd.project?.zones?.length      || 0; },
    allNodesLength() { return this.$app.sltd.project?.all_nodes?.length  || 0; },

    dragged_node_id() {
      return this.draggedNodeId
    },

    dragged_zone_id() {
      return this.draggedZoneId;
    },

    dragged_cntrl_id() {
      return this.draggedControllerId;
    },


    filtered() {
      return this.filter_text != "";
    },

    drop_allowed() {
      return this.$appm.can_drop_dragged_on_target(this.hovered_area);
    },

    sorted_cntrls() {
      const cntrls = this.$app.sltd.project?.cntrls || [];

      const hasIdx = c => Number(c?.locals?.sortIndex ?? 0) > 0;

      const withIndex    = cntrls.filter(hasIdx);
      const withoutIndex = cntrls.filter(c => !hasIdx(c));

      const head = this.$util.sort_table(withIndex, 'sortIndex');
      const tail = this.$util.sort_table(withoutIndex, 'name');

      return [...head, ...tail];
    },

    select_mode() {
      return this.$ui.select_mode;
    },

    available_cntrls() {
      return this.$util.list_cntrls_for_project(
        this.$app.sltd.project,
        this.$txt.none_single
      );
    },

    padlocked() {
      return this.$app.sltd.project.edit_locked;
    },

    padlock_hint() {
      if (this.padlocked) return this.$txt.enable_prj_edits;
      else return this.$txt.disable_prj_edits;
    },

    dragging() {
      // console.log("dragged item", this.$app.dragged_item)
      return this.$app.dragged_item != null;
    },

    dragged_type() {
      if (!this.dragging) return "none";
      if (this.$app.dragged_item.virtual)
        return this.$app.dragged_item.drag_type;
      if (this.$util.is_node(this.$app.dragged_item)) return "node";
      if (this.$util.is_node_device(this.$app.dragged_item))
        return "nodedevice";
      if (this.$util.is_zone(this.$app.dragged_item)) return "zone";
      if (this.$util.is_project(this.$app.dragged_item)) return "project";
      if (this.$util.is_cntrl(this.$app.dragged_item)) return "cntrl";
      if (this.$util.is_cntrl_device(this.$app.dragged_item))
        return "cntrldevice";
      else return "unknown";
    },

    width_px() {
      return this.width + "px";
    }
  }
};
</script>

<style>
.drop-disabled {
  opacity: 0.35;
  filter: grayscale(1);
}
.drop-enabled {
  opacity: 1;
  filter: none;
}
.drop-dim-row {
  opacity: 0.35;
  filter: grayscale(1);
}
.controller-row {
  white-space: nowrap;
}
.controller-row.drop-dim-row {
  opacity: 0.35;
  filter: grayscale(1);
}
.fade-move {
  transition: transform 0.1s;
}

.is-dragging .drop-disabled { cursor: no-drop !important; }
.is-dragging .drop-enabled  { cursor: move !important; }

.tree-item-border-top {
  border-top: 2px solid #000000;
}
.tree-item-border-bottom {
  border-bottom: 2px solid #000000;
}
</style>
